/* eslint-disable @tencent/i18next/no-literal-string */
import {
    PresetChartType,
    DEFAULT_AXIS_FONT_SIZE,
    DEFAULT_LEGEND_FONT_SIZE,
    DEFAULT_SHAPE_PROPS,
    DEFAULT_TITLE_FONT_SIZE,
    INITIAL_AXES_INDEX,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';

import { transaction } from '@src/slide/domain/slide-app';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { setLeafNodesToValue } from '@src/slide/infrastructure/module/independent/object/object-path';
import { STDispBlanksAs, STLegendPos } from '../../Interface/chart-v3/sdk-model.interface';
import ChartDefaultValueFactory from './default-value-factory';
import { getChartPresetHandler, getChartPresetHandlerByChartBaseType } from './sub-chart-presets';
import type { IChartLegend, IChartLine, IChartPlotArea, IChartSpace, IChartTitle } from '../../Interface/chart-v3/chart-properties.interface';
import type { BaseChartPresetHandler, SeriesInitialProps } from '../chart-presets/sub-chart-presets/base';
import type { IChartData, IChartDataModel, ISubChartData, ISubChartSerData } from '@tencent/slide-sdk-model';
import type { TextProps } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v2/style.interface';
import type { DeepPartial } from '@src/slide/infrastructure/module/independent/ts-tools/deep-partial';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { LineJoin } from '@tencent/alloy-renderer';
import { IChartAxis } from '../../Interface/chart-v3/chart-properties.interface';
import { ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import merge from 'lodash/merge';
import {
    SubChartConverterFactory,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/plot-area/sub-chart/sub-chart-converter-factory';
import { GridlinesType } from '@src/slide/domain/ui-model/element-modeles/constant/chart';
import { ILineCap } from '../../property/border/border.interface';
import { presetColorPalettes } from './colors';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IParagraph } from '../../property/text/interface/editor-para.interface';

/*
    ChartModelFactory，用于 创建/修改 图表的Model
 */
export class ChartModelFactory {
    /**
     *  创建type对应的预设图表
     */
    public static createChartDataByType(
        type: PresetChartType,
        chartProps?: Partial<IChartModel>,
        seriesProps?: SeriesInitialProps,
        titleText?: string,
    ) {
        const chart: any = {};
        const presetHandler = getChartPresetHandler(type);

        if (chartProps?.title !== null) {
            chart.title = this.createTitle(chartProps?.title, titleText);
        }
        if (chartProps?.legend !== null) {
            chart.legend = this.createLegend(type, presetHandler, chartProps?.legend);
        }

        const chartSpace = this.createChartSpace();
        if (chartSpace) {
            chart.chartSpace = chartSpace;
        }
        chart.dispBlanksAs = STDispBlanksAs.gap;
        const plotArea = this.createPlotArea(type, chartSpace, presetHandler, seriesProps);
        if (plotArea) {
            chart.plotArea = plotArea;
        }
        // TODO 还需要补充一下其他的参数，比如chartModel.id
        return chart;
    }

    /**
     *  修改当前图表至目标类型图表
     */
    public static modifyChartType(currentModel: IChartModel, targetType: PresetChartType) {
        const presetHandler = getChartPresetHandler(targetType);
        if (!presetHandler) {
            console.error('[modifyChartType] ', targetType, '暂不支持对应图表类型的修改');
            return;
        }

        const currentSubChartModels = this.getSubChartOwnedData(currentModel);

        const newSubChartModel = presetHandler.modifySubChartType(
            currentSubChartModels,
            targetType,
            getChartPresetHandlerByChartBaseType,
            currentModel.chartSpace!,
        );
        const {
            removeProps: plotAreaRemoveProps, updateProps: plotAreaUpdateProps,
        } = presetHandler.modifyPlotArea({
            plotArea: currentModel.plotArea,
            newSubChart: newSubChartModel,
            targetType,
            getChartPresetHandlerByChartBaseType,
        });

        // 每次修改图表类型，都清掉图例的宽高，使之自适应宽高展示。不然会出现修改后Marker变长，导致展示不下的问题
        // office是重新计算了宽高，我们先用自适应代替
        const legendRemoveProps = this.clearLegendRect(currentModel);

        transaction(() => {
            const removeChartSdkData = {};
            const removeSubChartsData = new Array(currentModel.plotArea.charts.length).fill(null);
            const removeUiData = {
                plotArea: {
                    // 原来的charts数组需要全部delete
                    // V3  走removeSubChartApi，这里不需要赋值
                    charts: [],
                    ...plotAreaRemoveProps,
                },
                legend: legendRemoveProps,
            };

            Object.getPrototypeOf(currentModel).constructor.convertDataUiToSdk(removeUiData, removeChartSdkData, {});

            setLeafNodesToValue(removeChartSdkData, null);

            // 删除子表
            removeSubChartsData.forEach(() => {
                // 不带上index默认会删第一个，transaction里的apply是立即执行，如果传index会错位
                ((currentModel.sdkData as IChartDataModel)?.api as any)?.removeSubChart();
            });

            // 移除不属于chartSpaceMutationData的charts数组
            delete (removeChartSdkData as IChartData)?.chartSpace?.chart?.plotArea?.charts;
            // 删除其余属性
            (currentModel.sdkData as IChartDataModel)?.api?.updateProps(removeChartSdkData);

            const updateChartSdkData = {};
            const updateUiData = {
                plotArea: {
                    charts: [newSubChartModel],
                    ...plotAreaUpdateProps,
                },
            };

            // 按当前逻辑，在修改图表的时候，axes相关的变更都在PlotArea的remove和update上
            // 而v3的axes在subChart上，所以这里需要结合ui层的数据和diff，merge出一个axes的结果，然后赋值到newSubChart上
            const axesData = this.getMergedAxesData(currentModel, updateUiData, removeUiData);
            this.setAxesToSubChart(axesData, newSubChartModel);

            // 整体chartModel的updateProps做ui-sdk
            Object.getPrototypeOf(currentModel).constructor
                .convertDataUiToSdk(updateUiData, updateChartSdkData, {});

            // 从整体sdk的结果中获取子表完整数据
            // （新增、修改图表的数据前端就是全量的，不需要再和subChart.updateProps一样需要根据diff merge全量数据）
            const sdkSubChart = (updateChartSdkData as IChartData).chartSpace?.chart?.plotArea?.charts?.[0];
            ((currentModel.sdkData as IChartDataModel)?.api as any)?.addSubChart(sdkSubChart, 0);

            // 移除不属于chartSpaceMutationData的charts数组，再通过updateProps更新除子表外的其余属性
            delete (updateChartSdkData as IChartData).chartSpace?.chart?.plotArea?.charts;
            (currentModel.sdkData as IChartDataModel)?.api?.updateProps(updateChartSdkData);
        });

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.CHART,
            action: TDWActionType.MODIFY_CHART_TYPE,
            ver6: JSON.stringify({
                from: currentModel.plotArea.charts.map(chart => chart.type).join('-'),
                target: targetType,
            }),
        });
    }

    public static createAxisTitle(props?: Partial<IChartTitle>): IChartTitle {
        return {
            overlay: false,
            props: DEFAULT_SHAPE_PROPS,
            ...props,
            paragraphList: ChartDefaultValueFactory.createDefaultParagraph({ fontSize: DEFAULT_AXIS_FONT_SIZE }),
        };
    }

    public static createTitle(props?: Partial<IChartTitle>, titleText?: string): IChartTitle {
        const paragraphList = ChartDefaultValueFactory
            .createDefaultParagraph({ fontSize: DEFAULT_TITLE_FONT_SIZE });
        let text;
        if (titleText) {
            const defaultParagraph = paragraphList?.[0] as IParagraph;
            text = {
                paragraphList: [
                    {
                        ...defaultParagraph,
                        textRuns: [ChartDefaultValueFactory.createDefaultRun(titleText)],
                    },
                ],
                textBodyStyle: ChartDefaultValueFactory.createDefaultTextBody(),
            };
        }
        return {
            overlay: false,
            paragraphList,
            props: DEFAULT_SHAPE_PROPS,
            text,
            ...props,
        };
    }

    public static createLegend(
        type?: PresetChartType,
        chartPresetHandler?: BaseChartPresetHandler,
        useProps?: Partial<IChartLegend>,
    ): IChartLegend {
        const presetHandler = chartPresetHandler || getChartPresetHandler(type || PresetChartType.BAR_CLUSTERED);

        return {
            position: STLegendPos.b,
            overlay: false,
            props: DEFAULT_SHAPE_PROPS,
            paragraphList: ChartDefaultValueFactory.createDefaultParagraph({ fontSize: DEFAULT_LEGEND_FONT_SIZE }),
            ...presetHandler?.getLegendProps(),
            ...useProps,
        };
    }

    public static createTitleText(title: string, from?: 'chartTitle'|'axesTitle'): TextProps {
        const fontSize = from === 'axesTitle' ? DEFAULT_AXIS_FONT_SIZE : DEFAULT_TITLE_FONT_SIZE;
        const paragraphList = ChartDefaultValueFactory.createDefaultParagraph({ fontSize });
        (paragraphList[0].textRuns as any) = [
            { text: title, styles: { lang: 'ea' } },
        ];
        return {
            paragraphList,
            textBodyStyle: ChartDefaultValueFactory.createDefaultTextBody(),
        };
    }

    public static createGridlines(type: GridlinesType, props?): IChartLine {
        return {
            props: {
                style: {
                    ...DEFAULT_SHAPE_PROPS.style,
                    border: {
                        linecap: ILineCap.Butt,
                        linejoin: LineJoin.Round,
                        width: 1.5,
                        color: type === 'majorGridlines' ? '#D9D9D9' : '#F2F2F2',
                        tdColor: type === 'majorGridlines' ? new TDColor({
                            rgbaStr: '#D9D9D9',
                        }) : new TDColor({
                            rgbaStr: '#F2F2F2',
                        }),
                    },
                },
            },
        };
    }

    private static createPlotArea(
        type: PresetChartType,
        chartSpace: IChartSpace,
        chartPresetHandler?: BaseChartPresetHandler,
        seriesInitialProps?: SeriesInitialProps,
    ): IChartPlotArea | undefined {
        // TODO 这里暂时没有考虑组合图
        // TODO 还需要补充一下其他的参数，比如subChartModel.id
        const presetHandler = chartPresetHandler || getChartPresetHandler(type);
        if (!presetHandler) {
            console.error('[createPlotArea] ', type, '暂不支持对应图表类型的修改');
            return;
        }

        const subChart = presetHandler.createChartModel(type, chartSpace, seriesInitialProps);
        const axesPreset = presetHandler.generateAxesPreset(subChart, type);

        this.setAxesToSubChart(axesPreset, subChart);

        return {
            ...axesPreset,
            props: DEFAULT_SHAPE_PROPS,
            charts: [subChart],
        };
    }

    private static clearLegendRect(chartModel: IChartModel) {
        if (!chartModel.legend?.layout) {
            return undefined;
        }

        const removeProps: DeepPartial<Record<keyof IChartLegend, any>> = {
            layout: {
                width: null,
                height: null,
            },
        };

        return removeProps;
    }

    /**
     * 将subChart中关联的坐标轴，都set到subChart上（还原成Sdk数据）
     */
    private static setAxesToSubChart(axesPreset: {
        categoryAxes?: IChartAxis[];
        valueAxes?: IChartAxis[];
        dateAxes?: IChartAxis[]
    }, subChart: ISubChartModel) {
        const { categoryAxes = [], valueAxes = [], dateAxes = [] } = axesPreset;
        const { axisIds } = subChart;

        const axes: IChartAxis[] = [];
        if (categoryAxes && categoryAxes.length > 0) {
            categoryAxes.forEach((axis) => {
                if (axis && axisIds?.includes(axis.axId!)) {
                    axes.push({
                        ...axis,
                        type: 'category',
                    });
                }
            });
        }

        if (dateAxes && dateAxes.length > 0) {
            dateAxes.forEach((axis) => {
                if (axis && axisIds?.includes(axis.axId!)) {
                    axes.push({
                        ...axis,
                        type: 'date',
                    });
                }
            });
        }

        if (valueAxes && valueAxes.length > 0) {
            valueAxes.forEach((axis) => {
                if (axis && axisIds?.includes(axis.axId!)) {
                    axes.push({
                        ...axis,
                        type: 'value',
                    });
                }
            });
        }


        if (axes.length > 0) {
            subChart.axes = axes;
            subChart.axisIds = axes.map(axis => axis.axId!);

            // 目前在slide业务中，修改图表类型之后，一定只有一个子表。所以这里只需要在没有axesAddIndex的情况下添加初始值即可
            if (subChart.axesAddIndex === undefined) {
                subChart.axesAddIndex = INITIAL_AXES_INDEX;
            }
        }
    }

    // 3.0 的子表样式是继承过的，这里要拿原始数据，转成uiData 再去切换类型
    private static getSubChartOwnedData(chartModel: IChartModel): ISubChartModel[] {
        const subChartsOwnedSerProps = (chartModel.sdkData as IChartDataModel)
            // @ts-ignore
            .props?.chartSpace?.chart?.plotArea?.charts?.map(subChart => subChart.getOwnedSerProps());

        return subChartsOwnedSerProps?.map((subChartSerData, index) => {
            const subChart = chartModel.plotArea.charts[index];
            const uiData = { type: subChart.type };
            const converter = SubChartConverterFactory.getSubChartConverterByType(subChart.type);
            const sdkSubChartData = { ser: subChartSerData as  ISubChartSerData[] };

            // 单独转换series，因为只有series和series下的数据有继承逻辑
            converter?.convertSubChartDataSdkToUi(
                sdkSubChartData as ISubChartData,
                uiData as ISubChartModel,
            );

            const subChartModel = {
                ...chartModel.plotArea.charts[index],
                series: (uiData as ISubChartModel).series,
            };

            return subChartModel as ISubChartModel;
        }) || [];
    }

    // 按当前逻辑， 在修改图表的时候，改到轴的，都在PlotArea的remove和update上
    // 而v3的轴在subChart上，所以这里需要在ui层 merge出一个axes的结果，然后赋值到newSubChart上
    private static getMergedAxesData(currentModel: IChartModel, updateUiData, removeUiData) {
        const { categoryAxes, valueAxes, dateAxes } = currentModel.plotArea;
        let axesData: Pick<IChartPlotArea, 'categoryAxes' | 'valueAxes' | 'dateAxes'> = {
            categoryAxes,
            valueAxes,
            dateAxes,
        };
        const removeAxesData = {
            categoryAxes: removeUiData.plotArea.categoryAxes,
            valueAxes: removeUiData.plotArea.valueAxes,
            dateAxes: removeUiData.plotArea.dateAxes,
        };
        const updateAxesData = {
            categoryAxes: updateUiData.plotArea.categoryAxes,
            valueAxes: updateUiData.plotArea.valueAxes,
            dateAxes: updateUiData.plotArea.dateAxes,
        };

        merge(axesData, removeAxesData);
        // 过滤掉被删除的坐标轴
        axesData = {
            categoryAxes: axesData.categoryAxes?.filter(item => item),
            valueAxes: axesData.valueAxes?.filter(item => item),
            dateAxes: axesData.dateAxes?.filter(item => item),
        };
        merge(axesData, updateAxesData);

        return axesData;
    }

    private static createChartSpace(props?: Partial<IChartSpace>): IChartSpace {
        return {
            colorStyle: presetColorPalettes.colorful[0],
            props: {
                style: {
                    border: {
                        noFill: 1,
                    },
                    effect: {},
                    fillBackground: {
                        noFill: 1,
                    },
                },
            },
        };
    }
}
